import { formatDateTime } from '../../utils/formatDate';
import { bootLines, deviceLines } from '../support/diagnostics';
import { reportProblem } from '../support/problemReport';
import type { SyncErrorEntry } from './syncErrorLog';

/**
 * When a failing sync is worth reporting by itself.
 *
 * Not on the first failure. A replication fails for a tunnel, a server being
 * restarted, a tablet whose WLAN dropped for a moment — all of which fix
 * themselves within the next retry. What is worth hearing about is the failure
 * that is STILL there a moment later.
 *
 * Both conditions are needed: the count alone would fire during a five-second
 * outage, the time alone on a single failure recorded half a minute after the
 * app started.
 */
const REPORT_AFTER_COUNT = 3;
const REPORT_AFTER_MS = 30_000;

export function shouldReportSyncError(
    entry: Pick<SyncErrorEntry, 'count' | 'firstAt'>,
    now: number
): boolean {
    return (
        entry.count >= REPORT_AFTER_COUNT &&
        now - entry.firstAt >= REPORT_AFTER_MS
    );
}

/**
 * Report a sync that has been failing long enough to mean it.
 *
 * The collection is deliberately NOT part of the signature, and that is the
 * whole lesson of the first run: the server going away fails all sixteen
 * collections within the same second, so a per-collection fingerprint turned
 * one outage into sixteen faults and ten mails. What went wrong is the
 * server's message, not which table asked first.
 *
 * So the fingerprint is the direction, the code and the message, and the body
 * names every collection failing at that moment: "everything" and "only the
 * photos" are different faults, and the list says which one this is.
 *
 * Deliberately does NOT ask the replication for its state: this is called from
 * inside the error log, which the replication itself writes to.
 */
export function reportSyncError(
    entry: SyncErrorEntry,
    /** Everything failing right now, the reporting one included. */
    failing: readonly SyncErrorEntry[]
): void {
    const alike = failing.filter(
        (other) =>
            other.direction === entry.direction &&
            other.code === entry.code &&
            other.message === entry.message
    );
    const collections = [...new Set(alike.map((other) => other.collection))];
    const headline = `Synchronisierung schlägt fehl: ${entry.message}`.slice(
        0,
        200
    );
    const body = [
        headline,
        '',
        ...deviceLines(),
        `Verbindung: ${typeof navigator === 'undefined' || navigator.onLine ? 'online' : 'offline'}`,
        ...bootLines(),
        '',
        `Bereiche: ${collections.length} (${collections.join(', ')})`,
        `Richtung: ${entry.direction || '—'}`,
        `Code: ${entry.code || '—'}`,
        `Seit: ${formatDateTime(entry.firstAt)} (${entry.count}× bis ${formatDateTime(entry.lastAt)})`,
        '',
        entry.message,
        ...entry.details
    ].join('\n');

    void reportProblem({
        kind: 'sync',
        headline,
        signature: `sync|${entry.direction}|${entry.code}|${entry.message}`,
        body
    });
}
